import React, { useState, createContext, useEffect, useCallback, useMemo, Context } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { IdentityConfig, IdentityContextType, AuthUser } from '../types';

export function createIdentityContextProvider<TRole>(config: IdentityConfig<TRole>): {
  IdentityContext: Context<IdentityContextType<TRole>>;
  IdentityContextProvider: React.FC<{ children: React.ReactNode }>;
} {
  const IdentityContext = createContext<IdentityContextType<TRole>>({} as IdentityContextType<TRole>);

  const IdentityContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [isAdmin, setIsAdmin] = useState<boolean>(false);
    const [loadingRoles, setLoadingRoles] = useState<boolean>(true);
    const [assignedRoles, setAssignedRoles] = useState<TRole[]>([]);
    const { authState, oktaAuth } = useOktaAuth();

    /**
     * Determines if an Okta auth flow is currently in progress.
     */
    const isAuthInProgress = useCallback((): boolean => {
      return oktaAuth.transactionManager.exists();
    }, [oktaAuth]);

    const contextValue = useMemo(
      () => ({
        isAdmin,
        assignedRoles,
        loadingRoles,
      }),
      [isAdmin, assignedRoles, loadingRoles]
    );

    useEffect(() => {
      const onLogin = async () => {
        // During callback, wait for auth to complete but don't fetch roles yet
        if (window.location.pathname === '/login/callback') {
          return;
        }

        config.logger.info('Checking for access token');

        let accessToken = authState?.accessToken?.accessToken;
        let userQuery = () => oktaAuth.getUser();

        // No token and not logging in, try to get current user silently
        // This is used for session restoration, token refresh etc, like on page reload
        if (!accessToken && !isAuthInProgress()) {
          config.logger.info('No access token, getting token without prompt');

          try {
            const oktaTokenResponse = await oktaAuth.token.getWithoutPrompt({
              scopes: ['openid', 'email'],
            });

            // Use a different function for fetching user data
            userQuery = () =>
              oktaAuth.token.getUserInfo(oktaTokenResponse.tokens.accessToken, oktaTokenResponse.tokens.idToken);

            // Set the access token
            accessToken = oktaTokenResponse.tokens.accessToken?.accessToken;
          } catch (error) {
            config.logger.error('Failed to get silent token');
          }
        }

        if (accessToken) {
          config.logger.info('Getting user info and roles');
          try {
            // Validate user before fetching roles
            const oktaUser: AuthUser = await userQuery();
            if (oktaUser?.email) {
              const roles = await config.roleApi(accessToken);
              setIsAdmin(config.isAdminCheck(roles));
              setAssignedRoles(roles);
            } else {
              config.logger.warning('No user email found, unable to fetch roles');
              setAssignedRoles([]);
            }
          } catch (error) {
            config.logger.error('Error fetching user roles');
            // Set empty roles array on error to prevent indefinite loading
            setAssignedRoles([]);
          }
        }
        setLoadingRoles(false);
      };

      onLogin();
    }, [authState?.accessToken?.accessToken, oktaAuth, isAuthInProgress, config]);

    return <IdentityContext.Provider value={contextValue}>{children}</IdentityContext.Provider>;
  };

  return {
    IdentityContext,
    IdentityContextProvider,
  };
}
